import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Clapperboard, Loader2, Mic2, Monitor, ScrollText, Volume2, Video } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { parsePitchScriptPreview } from '@/lib/pitch/pitchScriptFormatter';
import { BackgroundTemplatePicker } from '@/components/videoEditor/BackgroundTemplatePicker';
import { PitchRecordStudio } from '@/components/pitch/PitchRecordStudio';
import { PitchModuleStrip } from '@/components/pitch/PitchModuleStrip';
import { PitchAddModuleDialog } from '@/components/pitch/PitchAddModuleDialog';
import { PitchFloatingRecordToolbar } from '@/components/pitch/PitchFloatingRecordToolbar';
import { PitchSceneCaptureControls } from '@/components/pitch/PitchSceneCaptureControls';
import { audioFileExtensionFromMime } from '@/lib/pitch/pitchCameraCompositor';
import { usePitchScreenRecorder } from '@/lib/pitch/usePitchScreenRecorder';
import { useDocumentPip } from '@/lib/pitch/useDocumentPip';
import { projectDefaultCapture, resolveSegmentConfig } from '@/lib/pitch/pitchSegmentConfig';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { readAgentHandoff } from '@/lib/agentHandoff';
import { agentCreatePath } from '@/lib/creationPlan';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const STEPS = ['setup', 'segments', 'record', 'finalize'];

const TIMED_HEADER_RE = /^\d{1,2}:\d{2}\s*[–—-]\s*\d{1,2}:\d{2}\s*\|\s*.+/m;

function scriptHasTimedHeaders(raw) {
  return TIMED_HEADER_RE.test(String(raw || ''));
}

export function PitchFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectId } = useParams();
  const [step, setStep] = useState('setup');
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [teleprompterMode, setTeleprompterMode] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [speakerCameraEnabled, setSpeakerCameraEnabled] = useState(false);
  const [systemAudioEnabled, setSystemAudioEnabled] = useState(false);
  const [backgroundTemplateId, setBackgroundTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [activeSegmentIdx, setActiveSegmentIdx] = useState(0);
  const [pendingTake, setPendingTake] = useState(null);
  const [uploadingTake, setUploadingTake] = useState(false);
  const [formattingScript, setFormattingScript] = useState(false);
  const [studioKey, setStudioKey] = useState(0);
  const [resuming, setResuming] = useState(false);
  const [studioRecording, setStudioRecording] = useState(false);
  const [recordStartedAt, setRecordStartedAt] = useState(0);
  const [addModuleAt, setAddModuleAt] = useState(null);
  const [addingModule, setAddingModule] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingModule, setDeletingModule] = useState(false);
  const studioControlsRef = useRef(null);
  const pip = useDocumentPip({ title: 'Twinnee · pitch recorder' });

  const agentHandoff = useMemo(
    () => (routeProjectId ? null : readAgentHandoff(location.state)),
    [location.state, routeProjectId],
  );
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    if (routeProjectId) return;
    const nav = getIntakeNavPrefill(location.state);
    if (!nav) return;
    const fromAgent = Boolean(readAgentHandoff(location.state));
    if (nav.title) setTitle(nav.title);
    // A brief is not a teleprompter script, so agent handoffs only use a real script answer.
    const pitchScript = fromAgent ? String(nav.prefill.pitchScript || '').trim() : nav.pitchScript;
    if (pitchScript) setScript(pitchScript);
    if (fromAgent) setHandoffReady(true);
  }, [location.state, routeProjectId]);

  useEffect(() => {
    if (!routeProjectId) return undefined;
    let cancelled = false;
    (async () => {
      setResuming(true);
      try {
        const { data } = await api.get(`/pitch/projects/${routeProjectId}`);
        if (cancelled) return;
        setProject({
          ...data.project,
          story_id: data.story_id || data.project?.story_id,
        });
        setTitle(data.project?.title || '');
        setScript(data.project?.raw_script || '');
        setTeleprompterMode(data.project?.recording_mode !== 'freestyle');
        setCameraEnabled(Boolean(data.project?.camera_enabled));
        setScreenShareEnabled(Boolean(data.project?.screen_share_enabled));
        setSpeakerCameraEnabled(Boolean(data.project?.speaker_camera_enabled));
        setSystemAudioEnabled(Boolean(data.project?.system_audio_enabled));
        setBackgroundTemplateId(data.project?.background_template_id || '');
        setStep('record');
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Could not load pitch project');
          navigate(agentCreatePath({ flowId: 'pitch_recording' }), { replace: true });
        }
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeProjectId, navigate]);

  const previewSegments = useMemo(
    () => (teleprompterMode ? parsePitchScriptPreview(script) : []),
    [script, teleprompterMode],
  );

  const segments = useMemo(
    () => (project ? (project.segments || []) : previewSegments),
    [project, previewSegments],
  );
  const activeSegment = segments[activeSegmentIdx] || null;
  const activeConfig = resolveSegmentConfig(activeSegment, project);
  const screen = usePitchScreenRecorder({
    withSpeaker: activeConfig.capture === 'screen' && activeConfig.speakerCamera,
    withSystemAudio: activeConfig.capture === 'screen' && activeConfig.systemAudio,
  });
  const anyCameraModule = segments.some((s) => resolveSegmentConfig(s, project).capture === 'camera');
  const moduleBusy = studioRecording || uploadingTake || loading;

  const handleStudioRecordingChange = useCallback((isRecording) => {
    setStudioRecording(isRecording);
    if (isRecording) setRecordStartedAt(Date.now());
  }, []);

  const { supported: pipSupported, pipWindow, open: openPipWindow, close: closePipWindow } = pip;
  const popOutToolbar = useCallback(() => {
    if (!pipSupported || pipWindow) return;
    const tall = activeConfig.recordingMode === 'teleprompter';
    openPipWindow({ width: 400, height: tall ? 320 : 180 }).catch((err) => {
      console.warn('Floating recorder window unavailable:', err);
    });
  }, [activeConfig.recordingMode, openPipWindow, pipSupported, pipWindow]);

  useEffect(() => {
    if (step !== 'record') closePipWindow();
  }, [step, closePipWindow]);

  const selectModule = useCallback((idx) => {
    setActiveSegmentIdx(idx);
    setPendingTake(null);
  }, []);

  const formatScriptWithLlm = async () => {
    if (!script.trim()) {
      toast.error('Paste your script first');
      return;
    }
    if (scriptHasTimedHeaders(script)) {
      toast.info('Script already has timed section headers');
      return;
    }
    setFormattingScript(true);
    try {
      const { data } = await api.post('/pitch/format-script', { script: script.trim() });
      setScript(data.formatted_script || script);
      toast.success(
        data.llm_used
          ? `Added ${data.segments?.length || 0} timed sections`
          : 'Script already formatted',
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI formatting failed');
    } finally {
      setFormattingScript(false);
    }
  };

  const createProject = async () => {
    if (!title.trim()) {
      toast.error('Enter a pitch title');
      return;
    }
    if (teleprompterMode && !script.trim()) {
      toast.error('Paste your script for teleprompter mode');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/pitch/projects', {
        title: title.trim(),
        script: script.trim(),
        recording_mode: teleprompterMode ? 'teleprompter' : 'freestyle',
        camera_enabled: cameraEnabled && !screenShareEnabled && !speakerCameraEnabled && !systemAudioEnabled,
        screen_share_enabled: screenShareEnabled || speakerCameraEnabled || systemAudioEnabled,
        speaker_camera_enabled: speakerCameraEnabled,
        system_audio_enabled: systemAudioEnabled,
        background_template_id:
          (screenShareEnabled || speakerCameraEnabled || systemAudioEnabled)
            ? null
            : (backgroundTemplateId || null),
        auto_format_llm: teleprompterMode && !scriptHasTimedHeaders(script),
      });
      setProject({
        ...data.project,
        story_id: data.story_id || data.project?.story_id,
      });
      setStep('record');
      navigate(`/pitch/${data.project.id}/record`, { replace: true });
      toast.success('Pitch project created — record your parts');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const uploadTake = useCallback(async (take, segment = activeSegment) => {
    const blobTake = take || pendingTake;
    if (!project?.id || !segment?.id || !blobTake?.audioBlob || blobTake.audioBlob.size < 64) return false;
    setUploadingTake(true);
    setLoading(true);
    try {
      const audioExt = audioFileExtensionFromMime(blobTake.audioMime);
      const fd = new FormData();
      fd.append('audio', blobTake.audioBlob, `take-audio.${audioExt}`);
      fd.append('duration_sec', String(blobTake.durationSec || 10));
      if (blobTake.videoBlob) {
        fd.append('video', blobTake.videoBlob, 'take-video.webm');
      }
      const { data } = await api.post(
        `/pitch/projects/${project.id}/segments/${segment.id}/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setProject(data.project);
      setPendingTake(null);
      toast.success(`Saved to cloud — "${segment.title}"`);
      const segs = data.project?.segments || segments;
      const segIdx = segs.findIndex((s) => s.id === segment.id);
      const nextIdx = segs.findIndex((s, i) => i > segIdx && !s.recording?.audio_url);
      if (nextIdx >= 0) setActiveSegmentIdx(nextIdx);
      return true;
    } catch (err) {
      setPendingTake(blobTake);
      toast.error(err.response?.data?.detail || 'Upload to server failed — tap Retry');
      return false;
    } finally {
      setUploadingTake(false);
      setLoading(false);
    }
  }, [activeSegment, pendingTake, project?.id, segments]);

  const handleRecorded = useCallback(async (take) => {
    setPendingTake(take);
    await uploadTake(take, activeSegment);
  }, [activeSegment, uploadTake]);

  const updateBackgroundTemplate = useCallback(async (templateId) => {
    setBackgroundTemplateId(templateId);
    if (!project?.id) return;
    try {
      const { data } = await api.patch(`/pitch/projects/${project.id}`, {
        background_template_id: templateId || null,
      });
      setProject(data.project);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save background');
    }
  }, [project?.id]);

  const teleprompterSaveRef = useRef(null);
  const updateSegmentTeleprompter = useCallback((segmentId, teleprompterText) => {
    if (!project?.id || !segmentId) return;
    setProject((prev) => {
      if (!prev?.segments) return prev;
      return {
        ...prev,
        segments: prev.segments.map((seg) => (
          seg.id === segmentId
            ? { ...seg, teleprompter_text: teleprompterText }
            : seg
        )),
      };
    });
    if (teleprompterSaveRef.current) clearTimeout(teleprompterSaveRef.current);
    teleprompterSaveRef.current = setTimeout(async () => {
      try {
        const { data } = await api.patch(
          `/pitch/projects/${project.id}/segments/${segmentId}`,
          { teleprompter_text: teleprompterText },
        );
        setProject(data.project);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not save script');
      }
    }, 600);
  }, [project?.id]);

  const updateSegmentCapture = useCallback(async (segmentId, next) => {
    if (!project?.id || !segmentId || !next) return;
    const payload = {
      capture: next.capture,
      speaker_camera: Boolean(next.speakerCamera),
      system_audio: Boolean(next.systemAudio),
    };
    setProject((prev) => {
      if (!prev?.segments) return prev;
      return {
        ...prev,
        segments: prev.segments.map((seg) => (
          seg.id === segmentId
            ? {
              ...seg,
              capture: payload.capture,
              speaker_camera: payload.speaker_camera,
              system_audio: payload.system_audio,
            }
            : seg
        )),
      };
    });
    try {
      const { data } = await api.patch(
        `/pitch/projects/${project.id}/segments/${segmentId}`,
        payload,
      );
      setProject(data.project);
      setStudioKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update scene capture');
    }
  }, [project?.id]);

  useEffect(() => () => {
    if (teleprompterSaveRef.current) clearTimeout(teleprompterSaveRef.current);
  }, []);

  const handleReRecord = useCallback(async (segment) => {
    if (!project?.id || !segment?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        `/pitch/projects/${project.id}/segments/${segment.id}/clear-recording`,
      );
      setProject(data.project);
      setPendingTake(null);
      setStudioKey((k) => k + 1);
      toast.message(`Ready to re-record "${segment.title}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not clear previous take');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  const addModule = useCallback(async (payload) => {
    if (!project?.id) return;
    setAddingModule(true);
    try {
      const { data } = await api.post(`/pitch/projects/${project.id}/segments`, payload);
      setProject(data.project);
      const segs = data.project?.segments || [];
      const idx = segs.findIndex((s) => s.id === data.segment?.id);
      if (idx >= 0) setActiveSegmentIdx(idx);
      setPendingTake(null);
      setAddModuleAt(null);
      toast.success(`Added "${data.segment?.title || 'module'}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add module');
    } finally {
      setAddingModule(false);
    }
  }, [project?.id]);

  const deleteModule = useCallback(async () => {
    const target = deleteTarget;
    if (!project?.id || !target?.id) return;
    setDeletingModule(true);
    try {
      const deletedIdx = segments.findIndex((s) => s.id === target.id);
      const { data } = await api.delete(`/pitch/projects/${project.id}/segments/${target.id}`);
      setProject(data.project);
      const remaining = data.project?.segments?.length || 0;
      setActiveSegmentIdx((cur) => {
        const next = deletedIdx >= 0 && deletedIdx < cur ? cur - 1 : cur;
        return Math.max(0, Math.min(next, remaining - 1));
      });
      setPendingTake(null);
      setDeleteTarget(null);
      toast.success(`Deleted "${target.title || 'module'}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete module');
    } finally {
      setDeletingModule(false);
    }
  }, [deleteTarget, project?.id, segments]);

  const stopSharingFromToolbar = useCallback(async () => {
    if (studioRecording && activeConfig.capture === 'screen') {
      await studioControlsRef.current?.stop?.();
    }
    screen.stopSharing();
  }, [activeConfig.capture, screen, studioRecording]);

  const finalize = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/pitch/projects/${project.id}/finalize`);
      setProject(data.project);
      toast.success('Timeline ready');
      navigate(data.editor_path || `/pitch/${project.id}/video-editor`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finalize failed');
    } finally {
      setLoading(false);
    }
  };

  const recordedCount = (project?.segments || []).filter((s) => s.recording?.audio_url).length;

  if (resuming) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p className="text-sm">Loading pitch project…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="Pitch recording"
        busy={loading}
        onStart={createProject}
        startLabel="Create & start recording"
        fields={[
          {
            id: 'title',
            label: 'Pitch title',
            valid: Boolean(title.trim()),
            render: () => (
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Seed round pitch" autoFocus />
            ),
          },
          ...(teleprompterMode
            ? [
                {
                  id: 'script',
                  label: 'Teleprompter script',
                  hint: 'Shown on the teleprompter while you record.',
                  valid: Boolean(script.trim()),
                  render: () => (
                    <Textarea value={script} onChange={(e) => setScript(e.target.value)} rows={8} placeholder="Paste your pitch script…" />
                  ),
                },
              ]
            : []),
        ]}
      />
      <div className={`mx-auto px-4 py-8 space-y-8 ${step === 'record' && activeConfig.capture === 'screen' ? 'max-w-5xl' : 'max-w-3xl'}`}>
        <header>
          <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-2">
            <Clapperboard className="w-4 h-4" /> Pitch recording
          </div>
          <h1 className="text-2xl font-heading font-bold">Record your pitch with voice, camera, or a shared screen</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-xl">
            Paste a timed script for teleprompter takes, or record freestyle. Each part saves audio plus
            an optional camera or screen recording, then maps onto your story timeline.
          </p>
        </header>

        {step === 'setup' ? (
          <motion.div className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div>
              <Label htmlFor="pitch-title">Pitch title</Label>
              <Input
                id="pitch-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Twinee investor pitch"
                className="mt-1"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <ScrollText className="w-4 h-4" /> Teleprompter from script
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Off = freestyle on-the-go recording without scrolling script
                </p>
              </div>
              <Switch checked={teleprompterMode} onCheckedChange={setTeleprompterMode} />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Camera recording
                </p>
                <p className="text-xs text-slate-500 mt-1">Separate video file synced to each audio take</p>
              </div>
              <Switch
                checked={cameraEnabled}
                onCheckedChange={(on) => {
                  setCameraEnabled(on);
                  if (on) setScreenShareEnabled(false);
                }}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Monitor className="w-4 h-4" /> Screen share recording
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Default for new scenes. You can change capture, speaker camera, and system audio per scene while recording.
                </p>
              </div>
              <Switch
                checked={screenShareEnabled}
                onCheckedChange={(on) => {
                  setScreenShareEnabled(on);
                  if (on) setCameraEnabled(false);
                }}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Speaker camera
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Default for scenes. Floats along the bottom of the shared screen. Override per scene later.
                </p>
              </div>
              <Switch
                checked={speakerCameraEnabled}
                onCheckedChange={(on) => {
                  setSpeakerCameraEnabled(on);
                  if (on) {
                    setScreenShareEnabled(true);
                    setCameraEnabled(false);
                  }
                }}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Volume2 className="w-4 h-4" /> System / tab audio
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Default for scenes. Mix tab/system sound with your mic. Override per scene later. In Chrome, choose a
                  Chrome Tab and enable “Share tab audio”.
                </p>
              </div>
              <Switch
                checked={systemAudioEnabled}
                onCheckedChange={(on) => {
                  setSystemAudioEnabled(on);
                  if (on) {
                    setScreenShareEnabled(true);
                    setCameraEnabled(false);
                  }
                }}
              />
            </div>

            {cameraEnabled && !screenShareEnabled ? (
              <BackgroundTemplatePicker
                value={backgroundTemplateId}
                onChange={setBackgroundTemplateId}
                label="Video background (optional)"
                className="rounded-lg border border-slate-700 p-4"
              />
            ) : null}

            {teleprompterMode ? (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <Label htmlFor="pitch-script">Script</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={formattingScript || !script.trim()}
                    onClick={formatScriptWithLlm}
                  >
                    {formattingScript ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <TwinneeLogo className="w-3.5 h-3.5" />
                    )}
                    Add time ranges with AI
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Use timed headers like{' '}
                  <code className="text-amber-300">0:00 – 0:20 | Section title</code> then body text,
                  or paste plain prose and use AI to add timings (~150 WPM).
                </p>
                <Textarea
                  id="pitch-script"
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  rows={14}
                  className="font-mono text-sm"
                  placeholder={'0:00 – 0:20 | Founder Introduction\n\nHello, I am...'}
                />
              </div>
            ) : null}

            {teleprompterMode && previewSegments.length ? (
              <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
                <p className="text-xs font-semibold text-slate-300 mb-2">
                  {previewSegments.length} part(s) detected
                </p>
                <ul className="space-y-2 text-sm text-slate-400">
                  {previewSegments.map((s) => (
                    <li key={s.order}>
                      <span className="text-amber-400/90">{s.time_range_label || s.title}</span>
                      {s.target_duration_sec ? (
                        <span className="text-slate-600"> · ~{s.target_duration_sec}s</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <Button onClick={createProject} disabled={loading} className="gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
              Continue to recording
            </Button>
          </motion.div>
        ) : null}

        {step === 'record' && project ? (
          <motion.div className="space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {anyCameraModule ? (
              <BackgroundTemplatePicker
                value={project.background_template_id || backgroundTemplateId || ''}
                onChange={updateBackgroundTemplate}
                label="Video background (camera modules)"
                className="rounded-lg border border-slate-700 p-4"
              />
            ) : null}
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Modules play in this order on the timeline. Use + to add a teleprompter or independent-audio
                module at the beginning, between any two, or at the end.
              </p>
              <PitchModuleStrip
                segments={segments}
                project={project}
                activeIdx={activeSegmentIdx}
                disabled={moduleBusy}
                onSelect={selectModule}
                onInsert={setAddModuleAt}
                onDelete={setDeleteTarget}
              />
            </div>

            {activeSegment ? (
              <div className="space-y-3">
                <PitchSceneCaptureControls
                  capture={activeConfig.capture}
                  speakerCamera={activeConfig.speakerCamera}
                  systemAudio={activeConfig.systemAudio}
                  disabled={moduleBusy}
                  onChange={(next) => updateSegmentCapture(activeSegment.id, next)}
                />
                <PitchRecordStudio
                  key={`${activeSegment.id}-${studioKey}-${project.background_template_id || backgroundTemplateId || 'none'}-${activeConfig.capture}-${activeConfig.speakerCamera}-${activeConfig.systemAudio}`}
                  segment={activeSegment}
                  recordingMode={activeConfig.recordingMode}
                  capture={activeConfig.capture}
                  speakerCameraEnabled={activeConfig.speakerCamera}
                  systemAudioEnabled={activeConfig.systemAudio}
                  screen={screen}
                  backgroundTemplateId={project.background_template_id || backgroundTemplateId || ''}
                  controlsRef={studioControlsRef}
                  onBeforeStart={popOutToolbar}
                  onRecordingChange={handleStudioRecordingChange}
                  onRecorded={handleRecorded}
                  onReRecord={() => handleReRecord(activeSegment)}
                  onTeleprompterChange={updateSegmentTeleprompter}
                />
              </div>
            ) : null}

            {uploadingTake ? (
              <p className="text-sm text-amber-300/90 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {activeConfig.capture === 'screen'
                  ? 'Uploading audio and screen recording to cloud storage…'
                  : activeConfig.capture === 'camera'
                    ? 'Uploading audio and video to cloud storage…'
                    : 'Uploading audio to cloud storage…'}
              </p>
            ) : null}

            {pendingTake && !uploadingTake ? (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => uploadTake()} disabled={loading}>
                  Retry upload to server
                </Button>
                <Button variant="outline" onClick={() => setPendingTake(null)}>
                  Discard
                </Button>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800">
              <Button
                variant="secondary"
                onClick={finalize}
                disabled={loading || recordedCount === 0}
                className="gap-2"
              >
                <Mic2 className="w-4 h-4" />
                Build timeline ({recordedCount}/{segments.length} modules)
              </Button>
            </div>

            <PitchAddModuleDialog
              open={addModuleAt !== null}
              onOpenChange={(open) => {
                if (!open && !addingModule) setAddModuleAt(null);
              }}
              position={addModuleAt ?? segments.length}
              segments={segments}
              defaultCapture={projectDefaultCapture(project)}
              defaultMode={project.recording_mode === 'freestyle' ? 'freestyle' : 'teleprompter'}
              defaultSpeakerCamera={Boolean(project.speaker_camera_enabled)}
              defaultSystemAudio={Boolean(project.system_audio_enabled)}
              submitting={addingModule}
              onSubmit={addModule}
            />

            <AlertDialog
              open={Boolean(deleteTarget)}
              onOpenChange={(open) => {
                if (!open && !deletingModule) setDeleteTarget(null);
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete “{deleteTarget?.title || 'this module'}”?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {deleteTarget?.recording?.audio_url
                      ? 'The module and its recorded take are removed from this pitch. Build the timeline again to update the editor.'
                      : 'The module is removed from this pitch.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deletingModule}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={deletingModule}
                    className="bg-red-600 hover:bg-red-500"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteModule();
                    }}
                  >
                    {deletingModule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Delete module
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <PitchFloatingRecordToolbar
              visible={studioRecording || screen.sharing || uploadingTake || Boolean(pipWindow)}
              pipWindow={pipWindow}
              pipSupported={pipSupported}
              onPopOut={popOutToolbar}
              recording={studioRecording}
              uploading={uploadingTake}
              sharing={screen.sharing}
              startedAt={recordStartedAt}
              moduleLabel={
                activeSegment
                  ? `${activeSegmentIdx + 1}/${segments.length} · ${activeSegment.title || `Part ${activeSegmentIdx + 1}`}`
                  : 'No module selected'
              }
              hasPrev={activeSegmentIdx > 0}
              hasNext={activeSegmentIdx < segments.length - 1}
              onPrev={() => selectModule(Math.max(0, activeSegmentIdx - 1))}
              onNext={() => selectModule(Math.min(segments.length - 1, activeSegmentIdx + 1))}
              canStart={Boolean(activeSegment) && !loading}
              onStart={() => {
                const start = studioControlsRef.current?.start;
                if (!start) {
                  toast.error('Recorder is not ready — wait a moment and try Start again');
                  return;
                }
                Promise.resolve(start()).catch((err) => {
                  toast.error(err?.message || 'Could not start recording');
                });
              }}
              onStop={() => {
                const stop = studioControlsRef.current?.stop;
                if (!stop) return;
                Promise.resolve(stop()).catch((err) => {
                  toast.error(err?.message || 'Could not stop recording');
                });
              }}
              onStopSharing={stopSharingFromToolbar}
              teleprompterText={activeSegment?.teleprompter_text || ''}
              showTeleprompter={activeConfig.recordingMode === 'teleprompter'}
            />
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}

export default PitchFlowPage;
